import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import type { ApiError } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../../components/WritingIntoAFiledMonth';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { humanize } from '../../lib/enums';
import { formatDate, formatHours, formatMoney, formatRateBp } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

interface StatutoryParam {
  id: string;
  code: string;
  value: number;
  kind: string;
  effectiveFrom: string;
  note?: string | null;
}

const PARAMS_PATH = '/admin/v1/payroll/statutory-params';

/** How a manager types a value, and how it is stored. Money is lempiras; a rate is basis points. */
type Measure = 'hours' | 'minutes' | 'percent' | 'money';

interface RuleDef {
  code: string;
  label: string;
  description: string;
  measure: Measure;
  /** The stored unit for this code. It is never the manager's choice — it belongs to the setting. */
  kind: 'rateBp' | 'floorMinor' | 'ceilingMinor' | 'thresholdMinutes';
  /** What happens while nothing is recorded. Only worth saying where the answer is not obvious. */
  whenUnset?: string;
}

/**
 * The settings behind the hours screens and the pay register, as the questions they answer.
 *
 * These live in a table keyed by identifiers like ORDINARY_DAY_MINUTES, and the screen used to show
 * exactly that: a Code column of shouted machine names, and a free-text box to type one into. Two
 * failures came out of it. A manager who mistyped the code created a value nothing on earth reads,
 * with a green "Saved" to confirm it. And a manager who picked the wrong Kind stored a percentage
 * where minutes were expected — the reader does not check, so an overtime premium meant to be 25%
 * would have been read as a rate of 100 times that.
 *
 * Naming every setting here removes both. The code and its unit come from this list; the manager
 * answers a question in the unit they think in.
 */
const WORKING_DAY_RULES: RuleDef[] = [
  {
    code: 'ORDINARY_DAY_MINUTES',
    label: 'A normal working day',
    description:
      'Days longer than this are pointed out when you approve hours. It does not change what anyone is paid.',
    measure: 'hours',
    kind: 'thresholdMinutes',
    whenUnset: 'Long days are not being pointed out at all.',
  },
  {
    code: 'SCHEDULE_VARIANCE_TOLERANCE_MINUTES',
    label: 'Allowed difference from the schedule',
    description:
      'How far a day may drift from the hours someone was given before it counts as short or long. Clocking in two minutes late is not usually worth anyone reading about.',
    measure: 'minutes',
    kind: 'thresholdMinutes',
  },
  {
    code: 'MAX_SHIFT_MINUTES',
    label: 'Longest believable shift',
    description:
      'A single stretch on the clock longer than this is treated as a forgotten punch rather than as hours worked, so nobody is paid for a clock left running overnight.',
    measure: 'hours',
    kind: 'thresholdMinutes',
  },
  {
    code: 'OVERTIME_PREMIUM_RATE',
    label: 'Extra paid for overtime',
    description:
      'On top of the normal rate, and only for people whose pay record says their overtime carries a premium.',
    measure: 'percent',
    kind: 'rateBp',
    whenUnset:
      'Overtime hours are recorded on the register but never priced — the register says so on each person it affects.',
  },
];

const DEDUCTION_RULES: RuleDef[] = [
  {
    code: 'IHSS_EM_EMPLOYEE_RATE',
    label: 'IHSS sickness and maternity — employee share',
    description: "Taken from the employee's pay.",
    measure: 'percent',
    kind: 'rateBp',
  },
  {
    code: 'IHSS_EM_EMPLOYER_RATE',
    label: 'IHSS sickness and maternity — your share',
    description: 'Paid by the business on top of wages.',
    measure: 'percent',
    kind: 'rateBp',
  },
  {
    code: 'IHSS_EM_CEILING',
    label: 'IHSS sickness and maternity — pay ceiling',
    description: 'Earnings above this are not charged.',
    measure: 'money',
    kind: 'ceilingMinor',
  },
  {
    code: 'IHSS_IVM_EMPLOYEE_RATE',
    label: 'IHSS pension — employee share',
    description: "Taken from the employee's pay.",
    measure: 'percent',
    kind: 'rateBp',
  },
  {
    code: 'IHSS_IVM_EMPLOYER_RATE',
    label: 'IHSS pension — your share',
    description: 'Paid by the business on top of wages.',
    measure: 'percent',
    kind: 'rateBp',
  },
  {
    code: 'IHSS_IVM_CEILING',
    label: 'IHSS pension — pay ceiling',
    description: 'Earnings above this are not charged.',
    measure: 'money',
    kind: 'ceilingMinor',
  },
  {
    code: 'RAP_EMPLOYEE_RATE',
    label: 'RAP — employee share',
    description: "Taken from the employee's pay.",
    measure: 'percent',
    kind: 'rateBp',
  },
  {
    code: 'RAP_EMPLOYER_RATE',
    label: 'RAP — your share',
    description: 'Paid by the business on top of wages.',
    measure: 'percent',
    kind: 'rateBp',
  },
  {
    code: 'RAP_IVM_FLOOR',
    label: 'RAP — pay it starts above',
    description: 'Only the part of monthly pay above this figure is charged.',
    measure: 'money',
    kind: 'floorMinor',
  },
];

const KNOWN = [...WORKING_DAY_RULES, ...DEDUCTION_RULES];

/** Stored integer → what a person reads. Zero is a real answer for a tolerance, not a missing one. */
function showValue(rule: RuleDef, value: number): string {
  switch (rule.measure) {
    case 'percent':
      return formatRateBp(value);
    case 'money':
      return formatMoney(value, 'HNL');
    case 'minutes':
      return value === 0 ? 'None — every minute counts' : formatHours(value);
    default:
      // A length of zero is a figure somebody entered, and it has to stay visible as a wrong
      // setting rather than take on the look of the "Not set" badge beside it. formatHours draws
      // that line for every screen now, so this row does not need its own version of it.
      return formatHours(value);
  }
}

/** Stored integer → the number the input box shows, in the unit the label promises. */
function toInput(rule: RuleDef, value: number): number {
  switch (rule.measure) {
    case 'percent':
      return value / 100;
    case 'money':
      return value / 100;
    case 'hours':
      return value / 60;
    default:
      return value;
  }
}

/** What the person typed → the integer the column stores. */
function toStored(rule: RuleDef, entered: number): number {
  switch (rule.measure) {
    case 'percent':
      return Math.round(entered * 100);
    case 'money':
      return Math.round(entered * 100);
    case 'hours':
      return Math.round(entered * 60);
    default:
      return Math.round(entered);
  }
}

const SUFFIX: Record<Measure, string> = {
  hours: ' hours',
  minutes: ' minutes',
  percent: '%',
  money: '',
};

function RuleRow({
  rule,
  current,
  upcoming,
  onChange,
}: {
  rule: RuleDef;
  current?: StatutoryParam;
  upcoming?: StatutoryParam;
  onChange: (rule: RuleDef, current?: StatutoryParam) => void;
}) {
  return (
    <Paper withBorder p="sm" radius="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <div style={{ minWidth: 0 }}>
          <Group gap="xs" align="baseline">
            <Text size="sm" fw={600}>
              {rule.label}
            </Text>
            {current ? (
              <Text size="sm" fw={700}>
                {showValue(rule, current.value)}
              </Text>
            ) : (
              <Badge color="yellow" variant="light">
                Not set
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {rule.description}
          </Text>
          {current ? (
            <Text size="xs" c="dimmed">
              In force since {formatDate(current.effectiveFrom)}
              {current.note ? ` · ${current.note}` : ''}
            </Text>
          ) : (
            rule.whenUnset && (
              <Text size="xs" c="yellow.8">
                {rule.whenUnset}
              </Text>
            )
          )}
          {upcoming && (
            <Text size="xs" c="blue.7">
              Changes to {showValue(rule, upcoming.value)} on {formatDate(upcoming.effectiveFrom)}
            </Text>
          )}
        </div>
        <Button size="xs" variant="light" onClick={() => onChange(rule, current)}>
          {current ? 'Change' : 'Set'}
        </Button>
      </Group>
    </Paper>
  );
}

/**
 * The rules the hours screens and the pay register run on, as a settings page rather than a table.
 *
 * Each of these is effective-dated on purpose: changing one adds a dated record, so a pay period
 * already worked keeps being read against the figure that was in force at the time. That is why the
 * editor asks for a start date rather than overwriting anything.
 */
export function PayrollRulesPanel() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<RuleDef | null>(null);
  const [value, setValue] = useState<number | string>('');
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const params = useQuery({
    queryKey: listKey(PARAMS_PATH),
    queryFn: () => rawApi<StatutoryParam[]>('GET', PARAMS_PATH),
  });

  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  const rows = params.data ?? [];
  // The endpoint answers newest-first within a code, so the first row that has already taken effect
  // is the one being used, and anything above it is a change with a date still to come.
  const inForce = new Map<string, StatutoryParam>();
  const scheduled = new Map<string, StatutoryParam>();
  for (const row of rows) {
    if (row.effectiveFrom.slice(0, 10) > today) {
      // Overwritten rather than kept, precisely because the list is newest-first: the LAST future
      // row for a code is the change that lands soonest. Keeping the first would announce a rise
      // dated next year while a different figure quietly takes effect next month.
      scheduled.set(row.code, row);
    } else if (!inForce.has(row.code)) {
      inForce.set(row.code, row);
    }
  }

  const knownCodes = new Set(KNOWN.map((r) => r.code));
  // Nothing on file is ever hidden. A value this screen has no question for is still somebody's
  // decision, and quietly dropping it would be worse than showing it plainly. Read from both maps,
  // not just what is in force: a figure whose only record is dated ahead is the one most worth
  // seeing, and taking it from `inForce` alone would show every stray code except that one.
  const unrecognised = [...new Set(rows.map((row) => row.code))]
    .filter((code) => !knownCodes.has(code))
    .map((code) => inForce.get(code) ?? scheduled.get(code))
    .filter((row): row is StatutoryParam => row !== undefined);

  /**
   * A statutory figure is effective-dated, so one dated back into a month the accountant has filed
   * restates what that month cost in wages. The server refuses it once naming the month; this takes
   * the reason and sends the same figure again — see `WritingIntoAFiledMonth`.
   */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const save = useMutation({
    mutationFn: (input: { filedMonthReason?: string } = {}) => {
      if (!editing) throw new Error('nothing to save');
      return rawApi('POST', PARAMS_PATH, {
        code: editing.code,
        kind: editing.kind,
        value: toStored(editing, Number(value)),
        effectiveFrom: effectiveFrom ?? today,
        note: note || undefined,
        // Attached only once the server has asked for it — see the dialog for why never before.
        ...(input.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: input.filedMonthReason } : {}),
      });
    },
    onSuccess: () => {
      setFiledMonthWarning(null);
      void queryClient.invalidateQueries({ queryKey: listKey(PARAMS_PATH) });
      const saved = editing;
      setEditing(null);
      notifyDone('Saved', `${saved?.label} is updated.`);
    },
    onError: (err: Error) => {
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      notifyError(err, 'save this figure');
    },
  });

  const openEditor = (rule: RuleDef, current?: StatutoryParam) => {
    setValue(current ? toInput(rule, current.value) : '');
    setEffectiveFrom(today);
    setNote('');
    setEditing(rule);
  };

  const group = (title: string, description: string, rules: RuleDef[]) => (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>{title}</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm">
        {description}
      </Text>
      <Stack gap="xs">
        {rules.map((rule) => (
          <RuleRow
            key={rule.code}
            rule={rule}
            current={inForce.get(rule.code)}
            upcoming={scheduled.get(rule.code)}
            onChange={openEditor}
          />
        ))}
      </Stack>
    </Paper>
  );

  return (
    <>
      {params.isError && (
        <Alert color="red" title="Could not load these figures" mb="md">
          Nothing has been lost — this is only a display problem.
        </Alert>
      )}

      {group(
        'Working day rules',
        'What counts as a normal day, and what the hours screen points out to you. Changing one takes effect from the date you give it, so a pay period already worked keeps its old figures.',
        WORKING_DAY_RULES,
      )}

      {group(
        'Contributions and deductions',
        'The official rates and ceilings, which change by decree — check them against the current notice. They are kept here for the accountant: the pay register shows gross pay, before any of these.',
        DEDUCTION_RULES,
      )}

      {unrecognised.length > 0 && (
        <Paper withBorder p="md" radius="md" mb="md">
          <Title order={5}>Other figures on file</Title>
          <Text size="sm" c="dimmed" mt={2} mb="sm">
            Recorded at some point but not used by any of the rules above.
          </Text>
          <Stack gap={4}>
            {unrecognised.map((row) => (
              <Group key={row.id} gap="xs">
                <Text size="sm">{humanize(row.code)}</Text>
                <Text size="sm" fw={600}>
                  {row.kind === 'rateBp'
                    ? formatRateBp(row.value)
                    : row.kind === 'thresholdMinutes'
                      ? formatHours(row.value)
                      : formatMoney(row.value, 'HNL')}
                </Text>
                <Text size="xs" c="dimmed">
                  {/* "since" would be a lie about a figure dated ahead, and a wrong tense here is
                      how somebody reads a change as already applied. */}
                  {row.effectiveFrom.slice(0, 10) > today ? 'from' : 'since'} {formatDate(row.effectiveFrom)}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}

      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={editing?.label ?? ''}
        closeOnClickOutside={false}
      >
        {editing && (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {editing.description}
            </Text>
            <NumberInput
              label={editing.label}
              value={value}
              onChange={setValue}
              min={0}
              suffix={SUFFIX[editing.measure]}
              decimalScale={editing.measure === 'minutes' ? 0 : 2}
              thousandSeparator={editing.measure === 'money' ? ',' : undefined}
              step={editing.measure === 'hours' ? 0.5 : 1}
              autoFocus
            />
            <DateInput
              label="In force from"
              description="Hours and pay before this date keep the figure that applied then"
              value={effectiveFrom}
              onChange={setEffectiveFrom}
              valueFormat="D MMM YYYY"
            />
            <TextInput
              label="Note"
              description="Optional — where the figure came from"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditing(null)} disabled={save.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => save.mutate({})}
                loading={save.isPending}
                disabled={value === '' || !Number.isFinite(Number(value))}
              >
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => save.mutate({ filedMonthReason })}
      />
    </>
  );
}
